export function normalizeApplicationDetails(details) {
  if (!details || Array.isArray(details) || typeof details !== "object") {
    return {};
  }
  return details;
}

export function getInitialCostStateFromDetails(details) {
  const record = normalizeApplicationDetails(details);
  const hasBaseCost =
    record.base_cost !== null &&
    record.base_cost !== undefined &&
    record.base_cost !== "";

  return {
    cost: hasBaseCost
      ? String(record.base_cost)
      : record.professional_cost !== null &&
          record.professional_cost !== undefined &&
          record.professional_cost !== ""
        ? String(record.professional_cost)
        : "",
    currency: record.currency || "",
    tax: record.gst_status || "",
  };
}

export function getInitialDepartmentFeeStateFromDetails(details) {
  const record = normalizeApplicationDetails(details);
  const departmentBaseFee =
    record.department_base_fee ??
    record.department_fee_base_cost ??
    record.department_base_cost;
  const hasBaseFee =
    departmentBaseFee !== null &&
    departmentBaseFee !== undefined &&
    departmentBaseFee !== "";

  const inferDepartmentFeeTax = () => {
    const savedTax =
      record.department_fee_gst_status || record.department_gst_status;
    if (savedTax) return savedTax;

    const base = Number(departmentBaseFee);
    const total = Number(record.department_fee);
    const gst = Number(record.department_fee_gst_amount);
    const pct = Number(record.department_fee_gst_percentage);
    if (!pct || pct <= 0) return "notax";
    if (base > 0 && gst > 0 && Math.abs(total - base - gst) < 0.01) {
      return "exclusive";
    }
    if (base > 0 && Math.abs(total - base) < 0.01) {
      return "inclusive";
    }
    return "";
  };

  return {
    cost: hasBaseFee ? String(departmentBaseFee) : "",
    currency: record.department_fee_currency || "",
    tax: inferDepartmentFeeTax(),
  };
}

export function getEffectiveBaseCost(cost, details) {
  if (cost !== "" && cost !== null && cost !== undefined) {
    return cost;
  }
  return "";
}

export function getEffectiveCurrency(currency, details) {
  return currency || normalizeApplicationDetails(details).currency || "";
}

export function getEffectiveTax(tax, details) {
  return tax || normalizeApplicationDetails(details).gst_status || "";
}

export function getClientEmailFromRecord(record) {
  if (!record || typeof record !== "object") return "";
  return record.email || record.clientemail || record.client_email || "";
}

export function getClientNameFromRecord(record) {
  if (!record || typeof record !== "object") return "";
  return (
    record.name ||
    record.clientname ||
    record.client_name ||
    record.full_name ||
    record.fullName ||
    ""
  );
}

export function getClientIdFromRecord(record) {
  if (!record || typeof record !== "object") return undefined;
  return record.id || record.clientid || record.client_id;
}

export function getClientPhoneFromRecord(record) {
  if (!record || typeof record !== "object") return "";
  return record.mobile || record.phone || record.client_phone || "";
}

export function formatClientPhoneWithCountry(record) {
  const mobile = getClientPhoneFromRecord(record);
  if (!mobile) return "";
  if (/^\+\d+/.test(String(mobile))) return String(mobile);
  const country = getClientCountryFromRecord(record);
  if (country === "Pakistan") return `+92${mobile}`;
  return `+61${mobile}`;
}

export function getClientCountryFromRecord(record) {
  if (!record || typeof record !== "object") return "";
  return record.country || record.client_country || "";
}

export function sanitizeClientAddressForDisplay(
  record = {},
  explicitAddress = "",
) {
  if (!record || typeof record !== "object") return "";

  const description = String(record.description || "").trim();
  const city = String(record.city || "").trim();
  let address = String(
    explicitAddress ||
      record.address ||
      record.h_address ||
      record.street_address ||
      "",
  ).trim();

  if (description && address) {
    const lowerAddress = address.toLowerCase();
    const lowerDescription = description.toLowerCase();

    if (lowerAddress === lowerDescription) {
      address = "";
    } else {
      const commaPrefix = `${description}, `;
      if (lowerAddress.startsWith(commaPrefix.toLowerCase())) {
        address = address.slice(commaPrefix.length).trim();
      }

      const commaSuffix = `, ${description}`;
      if (lowerAddress.endsWith(commaSuffix.toLowerCase())) {
        address = address.slice(0, -commaSuffix.length).trim();
      }
    }
  }

  if (address && city) {
    const commaCitySuffix = `, ${city}`;
    if (
      address.toLowerCase().endsWith(commaCitySuffix.toLowerCase()) &&
      address.length > commaCitySuffix.length
    ) {
      const prefix = address
        .slice(0, address.length - commaCitySuffix.length)
        .trim();
      const looksLikeStreetAddress = /\d/.test(prefix);

      if (prefix && !looksLikeStreetAddress) {
        address = city;
      }
    }
  } else if (address.includes(",")) {
    const segments = address
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    if (segments.length === 2) {
      const [first, second] = segments;
      if (first && second && !/\d/.test(first)) {
        address = second;
      }
    }
  }

  if (address) return address;

  return resolveClientAddressFromRecord({
    ...record,
    description: "",
    address: "",
    h_address: "",
    street_address: "",
  });
}

export function resolveClientAddressFromRecord(record = {}) {
  if (!record || typeof record !== "object") return "";

  const parts = [
    record.address,
    record.h_address,
    record.street_address,
    record.street,
    record.city,
    record.state,
    record.postcode,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  const uniqueParts = [];
  parts.forEach((part) => {
    if (!uniqueParts.some((existing) => existing.toLowerCase() === part.toLowerCase())) {
      uniqueParts.push(part);
    }
  });

  const description = String(record.description || "").trim();
  const filteredParts = description
    ? uniqueParts.filter(
        (part) => part.toLowerCase() !== description.toLowerCase(),
      )
    : uniqueParts;

  return filteredParts.join(", ");
}

export function getClientAddressFromProfile(clientRecord, addressList = []) {
  const addresses = Array.isArray(addressList) ? addressList : [];
  const formatted = addresses
    .map((entry) => {
      if (!entry || typeof entry !== "object") return "";
      return [
        entry.h_address,
        entry.street,
        entry.address,
        entry.city,
        entry.state,
        entry.postcode,
      ]
        .filter(Boolean)
        .join(", ");
    })
    .filter(Boolean);

  if (formatted.length) return formatted.join("; ");

  return sanitizeClientAddressForDisplay(clientRecord);
}

export function buildClientDisplayProfile(clientRecord, addressList = []) {
  const country = getClientCountryFromRecord(clientRecord);
  const rawAddress = getClientAddressFromProfile(clientRecord, addressList);

  return {
    id: getClientIdFromRecord(clientRecord),
    fullName: getClientNameFromRecord(clientRecord),
    email: getClientEmailFromRecord(clientRecord),
    phone: formatClientPhoneWithCountry(clientRecord),
    address: formatInvoiceClientDisplayAddress({
      ...clientRecord,
      address: rawAddress,
      country,
    }),
    country,
    city: String(clientRecord?.city || "").trim(),
    description: String(clientRecord?.description || "").trim(),
  };
}

export function normalizeClientRecordSource(clientData) {
  if (!clientData || typeof clientData !== "object") {
    return { record: {}, addressList: [] };
  }

  const record =
    clientData.clients && typeof clientData.clients === "object"
      ? clientData.clients
      : clientData;

  const addressList =
    clientData.clientaddress ||
    clientData.clientAddress ||
    clientData.client_address ||
    record?.clientaddress ||
    [];

  return {
    record,
    addressList: Array.isArray(addressList) ? addressList : [],
  };
}

export function formatInvoiceClientDisplayAddress(profile = {}) {
  const address = sanitizeClientAddressForDisplay(profile, profile.address || "");
  const country = String(profile.country || "").trim();

  if (!address && country) return country;
  if (
    address &&
    country &&
    !address.toLowerCase().includes(country.toLowerCase())
  ) {
    return `${address}, ${country}`;
  }

  return address;
}

export function mergeInvoiceClientProfiles(snapshotProfile, liveProfile) {
  const snapshot = snapshotProfile || {};
  const live = liveProfile || {};

  const merged = {
    id: snapshot.id || live.id || null,
    fullName: snapshot.fullName || live.fullName || "",
    email: live.email || snapshot.email || "",
    phone: live.phone || snapshot.phone || "",
    country: live.country || snapshot.country || "",
    city: live.city || snapshot.city || "",
    description: live.description || snapshot.description || "",
    address: live.address || snapshot.address || "",
  };

  merged.address = formatInvoiceClientDisplayAddress(merged);
  return merged;
}

export function buildInvoiceClientPartyFields(clientProfile = {}) {
  const profile = clientProfile || {};
  const isAgencyBillTo = Boolean(profile.isAgencyBillTo);

  const fields = [
    { label: "Name", value: profile.fullName || "—" },
    { label: "Email", value: profile.email || "—" },
    {
      label: "Phone",
      value: profile.phone || (isAgencyBillTo ? "phone missing" : "—"),
    },
  ];

  if (!isAgencyBillTo) {
    fields.push({
      label: "Address",
      value: formatInvoiceClientDisplayAddress(profile) || "—",
    });
  }

  return fields;
}

function pickProviderPrimaryContact(personDetails = []) {
  const rows = Array.isArray(personDetails) ? personDetails : [];
  if (!rows.length) return null;

  return (
    rows.find(
      (row) =>
        String(row?.mobile || row?.landline || "").trim() ||
        String(row?.email || "").trim(),
    ) || rows[0]
  );
}

/** Bill-to profile for commission invoices addressed to education providers. */
export function buildProviderDisplayProfile(provider = {}, personDetails = []) {
  const primary = pickProviderPrimaryContact(personDetails);
  const city = String(provider?.city || "").trim();
  const country = String(provider?.country || "").trim();
  const streetAddress = String(
    provider?.address ||
      provider?.street_address ||
      provider?.h_address ||
      provider?.street ||
      "",
  ).trim();
  const email =
    String(provider?.email || provider?.contact_email || "").trim() ||
    String(primary?.email || "").trim();
  const phone =
    String(provider?.phone || provider?.mobile || "").trim() ||
    String(primary?.mobile || primary?.landline || "").trim();

  const profile = {
    id: provider?.id ?? null,
    fullName: String(provider?.name || provider?.fullName || "").trim(),
    email,
    phone,
    city,
    campus: city,
    country,
    description: String(provider?.note || provider?.description || "").trim(),
    address: streetAddress,
  };

  return profile;
}

export function buildProviderDisplayProfileFromApi(
  provider = {},
  personDetails = [],
) {
  const details =
    personDetails.length > 0
      ? personDetails
      : provider?.person_details || provider?.personDetails || [];

  return buildProviderDisplayProfile(provider, details);
}

export function getClientInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function isApplicationGstDetailsMissing(record) {
  const r = normalizeApplicationDetails(record);
  const status = String(r.gst_status || r.tax || "")
    .trim()
    .toLowerCase();

  if (
    !status ||
    status === "notax" ||
    status === "no tax" ||
    (status !== "inclusive" && status !== "exclusive")
  ) {
    return false;
  }

  const hasPct =
    r.gst_percentage !== null &&
    r.gst_percentage !== undefined &&
    r.gst_percentage !== "";
  const hasAmt =
    r.gst_amount !== null &&
    r.gst_amount !== undefined &&
    r.gst_amount !== "";

  return !hasPct || !hasAmt;
}

export function resolveGstModeType({
  gstStatus,
  ledgerExclusive = 0,
  ledgerInclusive = 0,
  gstEntryType,
} = {}) {
  const candidates = [gstStatus, gstEntryType]
    .map((value) => String(value || "").toLowerCase().trim())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (candidate === "exclusive") return "exclusive";
    if (candidate === "inclusive") return "inclusive";
    if (candidate === "notax" || candidate === "no tax") return "notax";
  }

  const exclusive = Number(ledgerExclusive) || 0;
  const inclusive = Number(ledgerInclusive) || 0;
  if (exclusive > 0 && inclusive <= 0) return "exclusive";
  if (inclusive > 0 && exclusive <= 0) return "inclusive";
  if (exclusive > 0 && inclusive > 0) {
    return exclusive >= inclusive ? "exclusive" : "inclusive";
  }

  return "notax";
}

export function formatGstModeLabel(gstType) {
  const normalized = String(gstType || "").toLowerCase().trim();
  if (normalized === "exclusive") return "(Exclusive)";
  if (normalized === "inclusive") return "(Inclusive)";
  return "";
}

export function inferGstModeFromAmounts({
  gstAmount = 0,
  subtotal = 0,
  totalAmount = 0,
} = {}) {
  const gst = Number(gstAmount) || 0;
  const sub = Number(subtotal) || 0;
  const total = Number(totalAmount) || 0;
  if (gst <= 0) return "";

  const tolerance = Math.max(1, Math.abs(total) * 0.001);

  if (Math.abs(total - (sub + gst)) <= tolerance) return "exclusive";
  if (Math.abs(total - sub) <= tolerance) return "inclusive";
  return "";
}

export function resolveInvoiceGstMode({
  gstInfo,
  subtotal = 0,
  totalAmount = 0,
  gstAmount = 0,
} = {}) {
  const resolvedFromStatus = resolveGstModeType({
    gstStatus: gstInfo?.type,
    ledgerExclusive: gstInfo?.ledgerExclusive,
    ledgerInclusive: gstInfo?.ledgerInclusive,
    gstEntryType: gstInfo?.gstEntryType,
  });

  if (resolvedFromStatus === "exclusive" || resolvedFromStatus === "inclusive") {
    return resolvedFromStatus;
  }

  const inferred = inferGstModeFromAmounts({ gstAmount, subtotal, totalAmount });
  if (inferred) return inferred;

  return resolvedFromStatus;
}

export function buildInvoiceGstLabel({
  gstInfo,
  gstPercentage = 0,
  gstAmount = 0,
  subtotal = 0,
  totalAmount = 0,
} = {}) {
  const hasGst = Number(gstAmount) > 0;
  const percentage = hasGst ? Number(gstPercentage) || 0 : 0;

  if (!hasGst) {
    return `TOTAL GST ${percentage}%`;
  }

  const directModeLabel =
    gstInfo?.gstModeLabel || formatGstModeLabel(gstInfo?.type);
  if (directModeLabel) {
    return `TOTAL GST ${percentage}% ${directModeLabel}`;
  }

  const mode = resolveInvoiceGstMode({
    gstInfo,
    subtotal,
    totalAmount,
    gstAmount,
  });
  const modeLabel = formatGstModeLabel(mode);

  return modeLabel
    ? `TOTAL GST ${percentage}% ${modeLabel}`
    : `TOTAL GST ${percentage}%`;
}

export function buildLocalInvoiceNo(appId, clientId) {
  if (!appId) return "";
  if (!clientId) return String(appId);
  return `${appId}-${clientId}`;
}

export function amountsMatch(left, right) {
  if (left === "" || left === null || left === undefined) {
    return right === "" || right === null || right === undefined;
  }
  if (right === "" || right === null || right === undefined) {
    return false;
  }
  return Math.abs(Number(left) - Number(right)) < 0.005;
}
